/**
 * ReportIssueModal
 * Shared modal for creating issues from multiple entry points:
 * - Booking Items tab (per-item action)
 * - Return Report detail (per-item "Create Issue")
 * - Inventory Maintenance tab ("Report Issue")
 * - Global Issues page ("Report Issue")
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X, AlertTriangle, Upload, Trash2, Loader2, Link as LinkIcon, Search, Calendar
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { createIssue, uploadIssuePhoto, getLastBookingForItem } from '../services/issues.service';
import { getProducts } from '../services/products.service';
import { getInventoryItems } from '../services/inventory.service';
import { useToast } from '../contexts/ToastContext';

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onIssueCreated?: (issue: any) => void;
  initialData?: {
    merchant_id: string;
    location_id?: string | null;
    inventory_item_id?: string | null;
    product_type_id?: string | null;
    booking_id?: string | null;
    booking_item_return_id?: string | null;
    source?: 'internal' | 'customer_return';
    title?: string;
    description?: string;
    severity?: 'low' | 'medium' | 'high' | 'critical';
    status?: string;
    item_label?: string; // Display name for the item
  };
}

interface InventoryItemOption {
  id: string;
  item_number: string;
  nr: number;
  product_name?: string;
  product_type_id?: string;
  location_id?: string;
  location_name?: string;
}

interface ProductTypeOption {
  id: string;
  product_name: string;
  product_type: string;
  location_name?: string;
}

export default function ReportIssueModal({
  isOpen,
  onClose,
  onIssueCreated,
  initialData,
}: ReportIssueModalProps) {
  const { t } = useTranslation();
  const { merchantId, accessControl } = useAuth();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState<'internal' | 'customer_return'>('internal');
  const [status, setStatus] = useState('reported');
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [repairDueDate, setRepairDueDate] = useState('');

  // Last booking for inventory item (optional link)
  const [lastBooking, setLastBooking] = useState<any>(null);
  const [linkLastBooking, setLinkLastBooking] = useState(false);

  // Optional product type & inventory item selectors (when no context pre-filled)
  const [productTypes, setProductTypes] = useState<ProductTypeOption[]>([]);
  const [selectedProductTypeId, setSelectedProductTypeId] = useState('');
  const [productTypeAutoFilled, setProductTypeAutoFilled] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItemOption[]>([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // Determine if we need to show optional selectors (no context pre-filled)
  const hasItemContext = !!initialData?.inventory_item_id || !!initialData?.item_label;
  const showItemSelector = !hasItemContext;
  const showProductTypeSelector = !initialData?.product_type_id && !hasItemContext;

  useEffect(() => {
    if (isOpen && initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setSeverity(initialData.severity || 'medium');
      setSource(initialData.source || 'internal');
      setStatus(initialData.status || 'reported');
      setBookingId(initialData.booking_id || null);
      setCategory('');
      setPhotos([]);
      setRepairDueDate('');
      setLinkLastBooking(false);
      setLastBooking(null);
      setSelectedProductTypeId('');
      setSelectedInventoryItemId('');
      setSelectedLocationId('');
      setInventorySearch('');
      setProductTypeAutoFilled(false);

      // If coming from inventory maintenance (no booking), fetch last booking
      if (initialData.inventory_item_id && !initialData.booking_id) {
        getLastBookingForItem(initialData.inventory_item_id).then(({ data }) => {
          if (data) setLastBooking(data);
        });
      }

      // Load product types and inventory items when no context is pre-filled
      if (showItemSelector || showProductTypeSelector) {
        loadDropdownData();
      }
    }
  }, [isOpen, initialData]);

  async function loadDropdownData() {
    const mId = initialData?.merchant_id || merchantId;
    if (!mId) return;

    if (showProductTypeSelector) {
      setLoadingProducts(true);
      const { data } = await getProducts(accessControl, {});
      if (data) {
        setProductTypes(data.map((p: any) => ({
          id: p.id,
          product_name: p.product_name,
          product_type: p.product_type,
          location_name: p.location_name || p.locations?.location_name,
        })));
      }
      setLoadingProducts(false);
    }

    if (showItemSelector) {
      setLoadingItems(true);
      const { data } = await getInventoryItems(accessControl, { active: true });
      if (data) {
        setInventoryItems(data.map((item: any) => ({
          id: item.id,
          item_number: item.item_number,
          nr: item.nr,
          product_name: item.product_name,
          product_type_id: item.product_type_id,
          location_id: item.location_id,
          location_name: item.location_name,
        })));
      }
      setLoadingItems(false);
    }
  }

  function handleSelectInventoryItem(item: InventoryItemOption) {
    setSelectedInventoryItemId(item.id);
    setInventorySearch(item.item_number);

    // Auto-fill product type and location from inventory item
    if (showProductTypeSelector && item.product_type_id) {
      setSelectedProductTypeId(item.product_type_id);
      setProductTypeAutoFilled(true);
    }
    if (item.location_id) {
      setSelectedLocationId(item.location_id);
    }
  }

  function handleClearInventoryItem() {
    setSelectedInventoryItemId('');
    setInventorySearch('');
    setSelectedLocationId('');
    // Clear auto-filled product type
    if (productTypeAutoFilled) {
      setSelectedProductTypeId('');
      setProductTypeAutoFilled(false);
    }
  }

  // Group product types by location for the dropdown
  const productTypesByLocation = productTypes.reduce<Record<string, ProductTypeOption[]>>((acc, pt) => {
    const loc = pt.location_name || 'Unknown Location';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(pt);
    return acc;
  }, {});

  const handleAddPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPhotos(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      showToast(t('reportIssue.pleaseEnterTitle'), 'error');
      return;
    }

    const mId = initialData?.merchant_id || merchantId;
    if (!mId) {
      showToast(t('reportIssue.merchantRequired'), 'error');
      return;
    }

    setSaving(true);
    try {
      const { data: user } = await supabase.auth.getUser();
      const userId = user?.user?.id || null;

      const linkedBookingId = linkLastBooking && lastBooking ? lastBooking.id : bookingId;

      const { data: issue, error } = await createIssue({
        merchant_id: mId,
        location_id: initialData?.location_id || selectedLocationId || null,
        inventory_item_id: initialData?.inventory_item_id || selectedInventoryItemId || null,
        product_type_id: initialData?.product_type_id || selectedProductTypeId || null,
        booking_id: linkedBookingId || null,
        booking_item_return_id: initialData?.booking_item_return_id || null,
        title: title.trim(),
        description: description.trim() || null,
        source,
        severity,
        category: category || null,
        status,
        repair_due_date: repairDueDate || null,
        confirmed_at: status === 'confirmed' ? new Date().toISOString() : null,
        confirmed_by: status === 'confirmed' ? userId : null,
        reported_by: userId,
      });

      if (error) {
        showToast(t('reportIssue.failedToCreate') + ': ' + error, 'error');
        setSaving(false);
        return;
      }

      // Upload photos
      if (issue && photos.length > 0 && userId) {
        for (const photo of photos) {
          await uploadIssuePhoto(issue.id, mId, userId, photo);
        }
      }

      showToast(t('reportIssue.createdSuccess'), 'success');
      onIssueCreated?.(issue);
      onClose();
    } catch (err) {
      showToast(t('reportIssue.errorOccurred'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const filteredItems = inventoryItems.filter(item =>
    item.item_number.toLowerCase().includes(inventorySearch.toLowerCase()) ||
    (item.product_name && item.product_name.toLowerCase().includes(inventorySearch.toLowerCase()))
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-md overflow-y-auto">
      <div className="bg-primary rounded-lg shadow-2xl max-w-lg w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-lg border-b border-border">
          <div className="flex items-center gap-md">
            <AlertTriangle className="w-5 h-5 text-warning" />
            <h2 className="text-lg font-semibold text-primary">
              {source === 'customer_return' ? t('reportIssue.createFromReturn') : t('reportIssue.title')}
            </h2>
          </div>
          <button onClick={onClose} className="btn-icon" disabled={saving}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-lg space-y-lg">
          {/* Item context */}
          {initialData?.item_label && (
            <div className="bg-secondary/10 rounded-lg p-md">
              <p className="text-sm text-secondary">{t('reportIssue.item')}</p>
              <p className="text-sm font-medium text-primary">{initialData.item_label}</p>
            </div>
          )}

          {/* Link to last booking (inventory maintenance entry point) */}
          {lastBooking && !initialData?.booking_id && (
            <div className="border border-border rounded-lg p-md">
              <label className="flex items-start gap-md cursor-pointer">
                <input
                  type="checkbox"
                  checked={linkLastBooking}
                  onChange={(e) => setLinkLastBooking(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-primary text-blue focus:ring-blue"
                />
                <div>
                  <div className="flex items-center gap-sm">
                    <LinkIcon className="w-4 h-4 text-secondary" />
                    <span className="text-sm font-medium text-primary">{t('reportIssue.linkToLastBooking')}</span>
                  </div>
                  <p className="text-xs text-secondary mt-xs">
                    #{lastBooking.order_id} — {lastBooking.customer_firstname} {lastBooking.customer_lastname}
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Optional Inventory Item selector (first — when no item context) */}
          {showItemSelector && (
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">{t('reportIssue.inventoryItem')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary pointer-events-none" />
                <input
                  type="text"
                  value={inventorySearch}
                  onChange={(e) => {
                    setInventorySearch(e.target.value);
                    if (!e.target.value) handleClearInventoryItem();
                  }}
                  className="input w-full pl-10"
                  placeholder={loadingItems ? t('common.loading') : t('reportIssue.searchByItemNumber')}
                  disabled={loadingItems}
                />
              </div>
              {inventorySearch && !selectedInventoryItemId && (
                <div className="border border-border rounded-lg mt-xs max-h-40 overflow-y-auto bg-primary">
                  {filteredItems.slice(0, 20).map(item => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleSelectInventoryItem(item)}
                      className="w-full text-left px-md py-sm hover:bg-secondary/10 text-sm"
                    >
                      <span className="font-medium text-primary">{item.item_number}</span>
                      {item.product_name && (
                        <span className="text-secondary ml-sm">— {item.product_name}</span>
                      )}
                    </button>
                  ))}
                  {filteredItems.length === 0 && (
                    <p className="px-md py-sm text-sm text-secondary">{t('reportIssue.noItemsFound')}</p>
                  )}
                </div>
              )}
              {selectedInventoryItemId && (
                <button
                  type="button"
                  onClick={handleClearInventoryItem}
                  className="text-xs text-blue hover:underline mt-xs"
                >
                  {t('reportIssue.clearSelection')}
                </button>
              )}
            </div>
          )}

          {/* Optional Product Type selector (categorized by location, auto-filled from item) */}
          {showProductTypeSelector && (
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('reportIssue.productType')}
                {productTypeAutoFilled && (
                  <span className="text-xs text-secondary font-normal ml-sm">({t('reportIssue.fromInventoryItem')})</span>
                )}
              </label>
              <select
                value={selectedProductTypeId}
                onChange={(e) => {
                  setSelectedProductTypeId(e.target.value);
                  setProductTypeAutoFilled(false);
                }}
                className="input w-full"
                disabled={loadingProducts}
              >
                <option value="">{loadingProducts ? t('common.loading') : t('reportIssue.selectProductType')}</option>
                {Object.entries(productTypesByLocation).map(([location, pts]) => (
                  <optgroup key={location} label={location}>
                    {pts.map(pt => (
                      <option key={pt.id} value={pt.id}>
                        {pt.product_name} ({pt.product_type})
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('reportIssue.titleLabel')} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input w-full"
              placeholder={t('reportIssue.titlePlaceholder')}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('reportIssue.descriptionLabel')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full"
              rows={3}
              placeholder={t('reportIssue.descriptionPlaceholder')}
            />
          </div>

          {/* Severity + Category row */}
          <div className="grid grid-cols-2 gap-md">
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">{t('reportIssue.severity')}</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as any)}
                className="input w-full"
              >
                <option value="low">{t('reportIssue.severityLow')}</option>
                <option value="medium">{t('reportIssue.severityMedium')}</option>
                <option value="high">{t('reportIssue.severityHigh')}</option>
                <option value="critical">{t('reportIssue.severityCritical')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">{t('reportIssue.category')}</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input w-full"
              >
                <option value="">{t('reportIssue.selectCategory')}</option>
                <option value="damage">{t('reportIssue.categoryDamage')}</option>
                <option value="malfunction">{t('reportIssue.categoryMalfunction')}</option>
                <option value="wear">{t('reportIssue.categoryWear')}</option>
                <option value="missing_part">{t('reportIssue.categoryMissingPart')}</option>
                <option value="cosmetic">{t('reportIssue.categoryCosmetic')}</option>
                <option value="other">{t('reportIssue.categoryOther')}</option>
              </select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              <span className="flex items-center gap-xs">
                <Calendar className="w-4 h-4" />
                {t('reportIssue.dueDate')}
              </span>
            </label>
            <input
              type="date"
              value={repairDueDate}
              onChange={(e) => setRepairDueDate(e.target.value)}
              className="input w-full"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('reportIssue.photos')}</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleAddPhotos}
              className="hidden"
            />
            <div className="flex flex-wrap gap-sm">
              {photos.map((photo, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border">
                  <img
                    src={URL.createObjectURL(photo)}
                    alt={photo.name}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(i)}
                    className="absolute top-0.5 right-0.5 w-5 h-5 bg-danger text-white rounded-full flex items-center justify-center"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 border-2 border-dashed border-secondary rounded-lg flex flex-col items-center justify-center text-secondary hover:border-primary hover:text-primary transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span className="text-xs mt-1">{t('reportIssue.add')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-md p-lg border-t border-border">
          <button onClick={onClose} className="btn btn-ghost" disabled={saving}>
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit} className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('reportIssue.creating')}
              </>
            ) : (
              t('reportIssue.createIssue')
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
